/**
  A datepicker for use in Safari/IE 11 that don't natively support input[type="date"]
  Inspired by the approach in https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
*/
import React from 'react'
import { Select } from 'components/inputs'
import { WbControlHint, WbControlError } from '@workbench/react/dist'

import { useNativeEvent } from './useNativeEvent'
import {
  useDateSelection,
  parseDateString,
  serializeDateString,
} from './useDateSelection'
import classes from './FallbackDatePicker.module.scss'
import { useTranslation } from 'react-i18next'

const MONTHS: { [key: number]: string } = {
  1: 'global.months.january',
  2: 'global.months.february',
  3: 'global.months.march',
  4: 'global.months.april',
  5: 'global.months.may',
  6: 'global.months.june',
  7: 'global.months.july',
  8: 'global.months.august',
  9: 'global.months.september',
  10: 'global.months.october',
  11: 'global.months.november',
  12: 'global.months.december',
}
const currentYear = new Date().getFullYear()
const INITIAL_VALUE = {
  day: 1,
  month: 1,
  year: currentYear,
}

interface DatePickerProps {
  id?: string
  label?: string
  min?: string
  max?: string
  defaultValue?: string
  hint?: string
  error?: string
  required?: boolean
  disabled?: boolean
  value?: string | number | readonly string[]
}

export const FallbackDatePicker: React.FunctionComponent<
  DatePickerProps & JSX.IntrinsicElements['input']
> = ({
  id,
  label,
  min,
  max,
  defaultValue,
  hint,
  error,
  required,
  disabled,
  value,
  ...inputProps
}) => {
  const { t } = useTranslation()
  const valueDate =
    value && typeof value === 'string' ? parseDateString(value) : null
  const defaultDate = defaultValue ? parseDateString(defaultValue) : null
  const minDate = min ? parseDateString(min) : null
  const initialValue = valueDate || defaultDate || minDate || INITIAL_VALUE

  const { dates, setYear, setMonth, setDay, days, months, years, touched } =
    useDateSelection(initialValue, min, max)

  /* Programatically dispatch a change event,
       to have onChange etc set on the element work as normal
    */
  const inputRef = useNativeEvent(serializeDateString(dates), touched)

  return (
    <div className={classes.fallbackDatePicker}>
      <label htmlFor={id} className={classes.fallbackLabel}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        name={id}
        type="text"
        hidden
        {...inputProps}
        // Unset required and pattern to prevent browsers from trying to validate this hidden field
        required={false}
        pattern={undefined}
      />

      <div className={classes.fallbackFields}>
        <span>
          <Select
            id={`${id}-day`}
            name={`${id}-day`}
            label={t('global.day')}
            value={dates.day}
            onChange={setDay}
            required={required}
            disabled={disabled}
            className={classes.select}
          >
            {days.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
        </span>
        <span>
          <Select
            id={`${id}-month`}
            name={`${id}-month`}
            label={t('global.month')}
            value={dates.month}
            onChange={setMonth}
            required={required}
            disabled={disabled}
            className={classes.select}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {t(MONTHS[m])}
              </option>
            ))}
          </Select>
        </span>
        <span>
          <Select
            id={`${id}-year`}
            name={`${id}-year`}
            label={t('global.year')}
            value={dates.year}
            onChange={setYear}
            required={required}
            disabled={disabled}
            className={classes.select}
          >
            {years.map((year) => (
              <option key={year}>{year}</option>
            ))}
          </Select>
        </span>
      </div>
      {hint && <WbControlHint>{hint}</WbControlHint>}
      {error && <WbControlError>{error}</WbControlError>}
    </div>
  )
}
